/**
 * Gestión → Compras → Proveedores (`/gestion/compras/proveedores`).
 *
 * Puerto de `Producto::proveedor` + `views/menu/proveedor/index.php` y del bundle
 * `supplier`: el padrón de **proveedores** con los totales de sus compras activas
 * (importe, pagado, pendiente y vencido), su **alta**, su **edición** y su **baja**
 * lógica.
 *
 * Los proveedores de **transporte** de los fletes son otra tabla
 * (`proveedor_transporte`) y se administran desde *Fletes*.
 */

import { ArrowLeft, Building2, Pencil, PowerOff, RefreshCw, RotateCcw, Search, TrendingDown, UserRoundPlus, Wallet, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { MenuAcciones } from '@/components/ui/menu-acciones';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePaginacion } from '@/hooks/use-paginacion';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency } from '@/lib/utils';

import {
  useActualizarProveedor,
  useEliminarProveedor,
  useProveedoresDetalle,
} from '../compras.api';
import type { FiltrosProveedores, ProveedorDetalle } from '../compras.types';
import { ProveedorDialog } from '../components/proveedor-dialogo';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Tarjeta del resumen de la cabecera. */
function Resumen({
  icono: Icono,
  etiqueta,
  valor,
  detalle,
  tono,
}: {
  icono: typeof Building2;
  etiqueta: string;
  valor: string;
  detalle: string;
  tono: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <span className={cn('grid size-10 shrink-0 place-items-center rounded-control', tono)}>
          <Icono className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">{etiqueta}</p>
          <p className="text-lg font-bold text-ink-900">{valor}</p>
          <p className="line-clamp-2 text-xs text-ink-500">{detalle}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function ProveedoresPage() {
  useDocumentTitle('Gestión · Proveedores');

  const navigate = useNavigate();

  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('1');
  const [edicion, setEdicion] = useState<ProveedorDetalle | null>(null);
  const [alta, setAlta] = useState(false);
  const [porDarBaja, setPorDarBaja] = useState<ProveedorDetalle | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  // La búsqueda libre espera a que el usuario deje de escribir.
  useEffect(() => {
    const temporizador = setTimeout(() => setBusqueda(texto.trim()), 300);

    return () => clearTimeout(temporizador);
  }, [texto]);

  const filtros = useMemo<FiltrosProveedores>(
    () => ({ search: busqueda, estado }),
    [busqueda, estado],
  );

  const consulta = useProveedoresDetalle(filtros);
  const darBaja = useEliminarProveedor();
  const reactivar = useActualizarProveedor();

  const proveedores = consulta.data?.proveedores ?? [];
  const resumen = consulta.data?.resumen;

  const paginacion = usePaginacion(proveedores, { clave: `${busqueda}|${estado}` });

  const mostrar = (mensaje: string) => {
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const reactivarProveedor = (proveedor: ProveedorDetalle) => {
    reactivar.mutate(
      {
        id: proveedor.id,
        datos: {
          ruc: proveedor.ruc ?? '',
          nombre: proveedor.proveedor ?? '',
          descripcion: proveedor.descripcion ?? undefined,
          telefono: proveedor.telefono ?? undefined,
        },
      },
      {
        onSuccess: () => mostrar(`Proveedor ${proveedor.proveedor ?? ''} reactivado.`),
        onError: (fallo) =>
          mostrar(
            fallo instanceof ApiError ? fallo.message : 'No se pudo reactivar el proveedor.',
          ),
      },
    );
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Proveedores</h1>
          <p className="mt-1 text-sm text-ink-500">
            Proveedores de compras con los totales de sus comprobantes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<ArrowLeft className="size-4" />}
            onClick={() => navigate('/gestion/compras')}
          >
            Volver a compras
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
          <Button size="sm" leftIcon={<UserRoundPlus className="size-4" />} onClick={() => setAlta(true)}>
            Nuevo proveedor
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          <span className={ICONO_EN_ALERTA}>
            <Building2 className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron cargar los proveedores">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {resumen ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
          <Resumen
            icono={Building2}
            etiqueta="Proveedores"
            valor={resumen.proveedores.toLocaleString('es-PE')}
            detalle={`${resumen.con_compras} con compras registradas`}
            tono="bg-brand-50 text-brand-600"
          />
          <Resumen
            icono={Wallet}
            etiqueta="Total comprado"
            valor={formatCurrency(resumen.total)}
            detalle={`Pagado ${formatCurrency(resumen.pagado)}`}
            tono="bg-emerald-50 text-emerald-600"
          />
          <Resumen
            icono={TrendingDown}
            etiqueta="Pendiente"
            valor={formatCurrency(resumen.pendiente)}
            detalle="Por pagar a los proveedores"
            tono="bg-amber-50 text-amber-600"
          />
          <Resumen
            icono={PowerOff}
            etiqueta="Vencido"
            valor={formatCurrency(resumen.vencido)}
            detalle="Comprobantes pasados de fecha"
            tono="bg-rose-50 text-rose-600"
          />
          <Resumen
            icono={Search}
            etiqueta="Filtrados"
            valor={proveedores.length.toLocaleString('es-PE')}
            detalle="Resultados con los filtros actuales"
            tono="bg-ink-100 text-ink-600"
          />
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm lg:col-span-2">
              <span className="font-medium text-ink-700">Buscar proveedor</span>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  className={`${CONTROL} pl-9`}
                  placeholder="RUC, nombre o descripción…"
                  value={texto}
                  onChange={(evento) => setTexto(evento.target.value)}
                />
                {texto ? (
                  <button
                    type="button"
                    className="absolute top-1/2 right-3 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                    aria-label="Limpiar la búsqueda"
                    onClick={() => setTexto('')}
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado</span>
              <Select className={CONTROL} value={estado} onChange={(evento) => setEstado(evento.target.value)}>
                <option value="1">Activos</option>
                <option value="2">De baja</option>
                <option value="todos">Todos</option>
              </Select>
            </label>

            <div className="flex items-end">
              <span className="pb-3 text-sm text-ink-500">
                {proveedores.length.toLocaleString('es-PE')}{' '}
                {proveedores.length === 1 ? 'proveedor' : 'proveedores'}
              </span>
            </div>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading && !consulta.isError ? (
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-3 py-2">Proveedor</th>
                    <th className="px-3 py-2">RUC</th>
                    <th className="px-3 py-2">Teléfono</th>
                    <th className="px-3 py-2 text-right">Compras</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Pendiente</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginacion.visibles.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-3 py-10 text-center text-sm text-ink-500">
                        No hay proveedores que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((proveedor) => (
                    <tr key={proveedor.id} className="border-t border-ink-100 hover:bg-brand-50/30">
                      <td className="max-w-[18rem] px-3 py-2">
                        <p className="truncate font-semibold text-ink-900" title={proveedor.proveedor ?? ''}>
                          {proveedor.proveedor ?? '—'}
                        </p>
                        {proveedor.descripcion ? (
                          <p className="truncate text-xs text-ink-500" title={proveedor.descripcion}>
                            {proveedor.descripcion}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-ink-700">{proveedor.ruc ?? '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-ink-600">
                        {proveedor.telefono ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                        {proveedor.compras.toLocaleString('es-PE')}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-ink-900">
                        {formatCurrency(proveedor.total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <p className="font-semibold text-ink-800">
                          {formatCurrency(proveedor.total_pendiente)}
                        </p>
                        {proveedor.total_vencido > 0 ? (
                          <p className="text-xs text-rose-600">
                            vencido {formatCurrency(proveedor.total_vencido)}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Badge variant={proveedor.estado === 1 ? 'success' : 'neutral'}>
                          {proveedor.estado === 1 ? 'ACTIVO' : 'DE BAJA'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            title="Editar el proveedor"
                            aria-label={`Editar ${proveedor.proveedor ?? 'el proveedor'}`}
                            onClick={() => setEdicion(proveedor)}
                          >
                            <Pencil className="size-4" />
                          </Button>

                          {proveedor.estado === 1 ? (
                            <MenuAcciones
                              etiqueta={`Más acciones de ${proveedor.proveedor ?? 'el proveedor'}`}
                              acciones={[
                                {
                                  etiqueta: 'Dar de baja',
                                  peligrosa: true,
                                  icono: <PowerOff className="size-3.5" aria-hidden="true" />,
                                  onClick: () => setPorDarBaja(proveedor),
                                },
                              ]}
                            />
                          ) : (
                            <Button
                              variant="outline"
                              size="icon"
                              title="Reactivar el proveedor"
                              aria-label={`Reactivar ${proveedor.proveedor ?? 'el proveedor'}`}
                              onClick={() => reactivarProveedor(proveedor)}
                            >
                              <RotateCcw className="size-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {!consulta.isLoading && !consulta.isError ? (
            <Paginador {...paginacion} etiqueta="proveedores" />
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Building2 className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Los datos del proveedor se usan en las compras y en el pago a proveedores. Los proveedores
          de <strong>transporte</strong> de los fletes son otro padrón y se administran desde{' '}
          <Link className="font-semibold text-brand-700 underline" to="/gestion/fletes">
            Fletes
          </Link>
          .
        </span>
      </p>

      <ProveedorDialog
        key={edicion ? `editar-${edicion.id}` : alta ? 'nuevo' : 'cerrado'}
        proveedor={edicion}
        abierto={edicion !== null || alta}
        onClose={() => {
          setEdicion(null);
          setAlta(false);
        }}
        onAviso={mostrar}
      />

      <ConfirmDialog
        open={porDarBaja !== null}
        title={`¿Dar de baja a ${porDarBaja?.proveedor ?? 'este proveedor'}?`}
        description="Es una baja lógica: sus compras y pagos se conservan y se puede reactivar después."
        confirmLabel="Dar de baja"
        loading={darBaja.isPending}
        onClose={() => setPorDarBaja(null)}
        onConfirm={() => {
          const proveedor = porDarBaja;

          setPorDarBaja(null);

          if (!proveedor) return;

          darBaja.mutate(proveedor.id, {
            onSuccess: (respuesta) => mostrar(respuesta.mensaje),
            onError: (fallo) =>
              mostrar(fallo instanceof ApiError ? fallo.message : 'No se pudo dar de baja.'),
          });
        }}
      />
    </div>
  );
}
